import React from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatCalendarDate } from '../utils/dateUtils';

/**
 * Popup for duplicate claimant, claim, or appointment warnings.
 * mode: 'alert' — informational (Got it)
 * mode: 'confirm' — user must choose Cancel or Continue anyway
 */
const DuplicateWarningModal = ({
  isOpen,
  type,
  data,
  mode = 'alert',
  onClose,
  onConfirm,
  onViewAppointment,
  onViewClaimant,
  onViewClaim
}) => {
  if (!isOpen || !data) return null;

  const isConfirm = mode === 'confirm';

  let title = 'Possible duplicate';
  let body = null;

  if (type === 'claimant') {
    title = 'Claimant name already exists';
    const total = data.total ?? 0;
    const matches = data.matches || [];
    body = (
      <div className="text-sm text-gray-600 space-y-3">
        <p>
          A claimant with this name already exists on your account ({total} match
          {total !== 1 ? 'es' : ''}). You can still continue if this is a different person.
        </p>
        {matches.length > 0 && (
          <div className="space-y-2">
            {matches.map((match) => {
              const dob = formatCalendarDate(match.date_of_birth);
              return (
                <div
                  key={match.id}
                  className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-1"
                >
                  <p className="font-medium text-gray-900">
                    {match.first_name} {match.last_name}
                  </p>
                  <div className="text-xs text-gray-600 space-y-0.5">
                    {dob && <p>Date of birth: {dob}</p>}
                    {match.phone && <p>Phone: {match.phone}</p>}
                    {match.language && <p>Language: {match.language}</p>}
                  </div>
                  {match.has_direct_access === false && (
                    <p className="text-xs text-amber-700 mt-1">
                      You are not the contact handler on this claimant&apos;s claims, but you can
                      still view their profile.
                    </p>
                  )}
                  {onViewClaimant && match.id && (
                    <button
                      type="button"
                      onClick={() => {
                        onViewClaimant(match.id, match);
                        onClose?.();
                      }}
                      className="text-amber-800 font-medium underline hover:text-amber-950 text-sm mt-1"
                    >
                      View existing claimant
                    </button>
                  )}
                </div>
              );
            })}
            {total > matches.length && (
              <p className="text-xs text-gray-500">
                Showing {matches.length} of {total} matches.
              </p>
            )}
          </div>
        )}
      </div>
    );
  } else if (type === 'claim') {
    title = 'Claim number already exists';
    const doi = formatCalendarDate(data.date_of_injury);
    body = (
      <div className="text-sm text-gray-600 space-y-3">
        <p>This claim number is already on file on your account.</p>
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-1">
          <p className="font-medium text-gray-900">
            {data.claim_number}
            {data.case_type ? ` — ${data.case_type}` : ''}
          </p>
          <div className="text-xs text-gray-600 space-y-0.5">
            {data.claimant_name && <p>Claimant: {data.claimant_name}</p>}
            {doi && <p>Date of injury: {doi}</p>}
            {data.file_status && <p>Status: {data.file_status}</p>}
            {data.contact_claims_handler_name && (
              <p>Contact handler: {data.contact_claims_handler_name}</p>
            )}
            {data.adjusters_assistant_name && (
              <p>Adjuster assistant: {data.adjusters_assistant_name}</p>
            )}
          </div>
          {data.has_direct_access === false && (
            <p className="text-xs text-amber-700 mt-1">
              You are not the contact handler or assistant on this claim. You can still view it,
              or choose to assign yourself as the adjuster on file.
            </p>
          )}
          {onViewClaim && data.id && (
            <button
              type="button"
              onClick={() => {
                onViewClaim(data);
                onClose?.();
              }}
              className="text-amber-800 font-medium underline hover:text-amber-950 text-sm mt-1"
            >
              View existing claim
            </button>
          )}
        </div>
      </div>
    );
  } else if (type === 'appointment') {
    title = 'Appointment already on file';
    const dateStr = formatCalendarDate(data.scheduled_date);
    const timeStr = data.scheduled_time ? String(data.scheduled_time).slice(0, 5) : null;
    body = (
      <div className="text-sm text-gray-600 space-y-2">
        <p>
          We already have an appointment on file: <strong>{data.job_number}</strong>
          {data.claimant_name ? (
            <>
              {' '}
              for <strong>{data.claimant_name}</strong>
            </>
          ) : null}
          {dateStr && timeStr ? (
            <>
              {' '}
              on {dateStr} at {timeStr}
            </>
          ) : null}
          {data.location_label ? <> at {data.location_label}</> : null}.
        </p>
        {onViewAppointment && data.id && (
          <button
            type="button"
            onClick={() => {
              onViewAppointment(data.id);
              onClose?.();
            }}
            className="text-amber-800 font-medium underline hover:text-amber-950"
          >
            View existing appointment
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="duplicate-warning-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={`w-full rounded-xl bg-white shadow-2xl ${
        (type === 'claimant' && (data.matches || []).length > 0) || type === 'claim'
          ? 'max-w-lg'
          : 'max-w-md'
      }`}>
        <div className="flex items-start gap-3 p-5 border-b border-gray-100">
          <div className="flex-shrink-0 rounded-full bg-amber-100 p-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="duplicate-warning-title" className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
            <div className="mt-2">{body}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex justify-end gap-3 p-4 bg-gray-50 rounded-b-xl">
          {isConfirm ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Continue anyway
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DuplicateWarningModal;
